import { jsx } from '@front10/base-elements';
/** @jsx jsx */
import React, { useRef, useContext, useState } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import Row from '../../../elements/Row';
import Col from '../../../elements/Col';
import SingleDatePicker from '../../../controls/DatePickerWidget/SingleDatePicker';
import SingleDateRangePicker from '../../../controls/DatePickerWidget/SingleDateRangePicker';
import OriginDestinationFilter from '../../../controls/InlineFilters/OriginDestinationFilter';
import Button from '../../../elements/Button';
import Box from '../../../elements/Box';
import OriginDestinationFilterMobile from '../../../controls/InlineFilters/OriginDestinationFilterMobile';
import NavigationHeader from '../../../elements/NavigationHeader';
import {
  enterFullMode,
  exitFullMode,
  notifyFullMode,
  notifyExitFullMode
} from '../../../containers/_commons/navigation';
import { DialogOverlay, DialogContent } from '@reach/dialog';
import useStyles from '@front10/helpers/dist/UseStyles';

const RouteFilters = ({
  availableFilters,
  showLabels,
  originDestinationSettings,
  dateRangeColumnSettings,
  dateRangeSettings,
  callToActionColumSettings = {},
  containerSettings,
  callToActionSettings = {},
  dateRange,
  locations,
  secondaryFilter = undefined,
  setCloseButtonFocus,
  closeButtonFocus,
  complementaryInformation,
  isMobile = false,
  originDestinationContainerSettings = {},
  headerLocation,
  headerSettings,
  containerOnFullScreenSettings,
  enableExperimentalNavigation = false,
  dialogSettings = {}
}) => {
  const originRef = useRef();
  const { translate } = useContext(Context);
  const locationComponents = {
    ...originDestinationSettings.components,
    origin: {
      inputFocusReferences: originRef,
      ...originDestinationSettings.components?.origin
    }
  };

  const [isClearOriginFocus, setIsClearOriginFocus] = useState(false);
  const [isClearDestinationFocus, setIsClearDestinationFocus] = useState(false);
  const enableAriaHiddenFooter = useRef(false);
  const setEnableAriaHiddenFooter = (value) => {
    enableAriaHiddenFooter.current = value;
  };

  const [navigation, setNavigation] = React.useState('all');
  const [isFullMode, setIsFullMode] = React.useState(false);

  const goBack = () => {
    setNavigation('all');
    notifyExitFullMode();
    if (useClassicNavigation) {
      exitFullMode(ref);
      setIsFullMode(false);
    }
  };

  const getNavigationProps = (
    key,
    { onClear, onFocus, onChange, onClick, avoidCloseFilter = false, ...rest }
  ) => {
    return {
      onClear: (event) => {
        if (onClear) {
          onClear(event);
        }
        setNavigation(key);
        if (!isFullMode) {
          notifyFullMode();
        }
        if (useClassicNavigation) {
          enterFullMode(ref);
          setIsFullMode(true);
        }
      },
      onFocus: (event) => {
        if (onFocus) {
          onFocus(event);
        }
        setNavigation(key);
        if (!isFullMode) {
          notifyFullMode();
        }
        if (useClassicNavigation) {
          enterFullMode(ref);
          setIsFullMode(true);
        }
      },
      onClick: (event) => {
        if (onClick) {
          onClick(event);
        }
        setNavigation(key);
        if (!isFullMode) {
          notifyFullMode();
        }
        if (useClassicNavigation) {
          enterFullMode(ref);
          setIsFullMode(true);
        }
      },
      onChange: (event) => {
        if (onChange) {
          onChange(event);
        }
        if (!Array.isArray(event)) {
          if (!avoidCloseFilter) {
            goBack();
          }
        }
      },
      ...rest
    };
  };

  const elements = {
    OriginDestination: (
      <OriginDestinationFilter
        key="OriginDestination"
        showLabels={showLabels}
        {...originDestinationSettings}
        components={locationComponents}
        setCloseButtonFocus={setCloseButtonFocus}
        closeButtonFocus={closeButtonFocus}
        {...locations}
        setIsClearOriginFocus={setIsClearOriginFocus}
        setIsClearDestinationFocus={setIsClearDestinationFocus}
        isClearOriginFocus={isClearOriginFocus}
        isClearDestinationFocus={isClearDestinationFocus}
        setEnableAriaHiddenFooter={setEnableAriaHiddenFooter}
      />
    ),
    OriginDestinationMobile: (
      <OriginDestinationFilterMobile
        key="OriginDestination"
        showLabels={showLabels}
        {...originDestinationSettings}
        components={locationComponents}
        setCloseButtonFocus={setCloseButtonFocus}
        closeButtonFocus={closeButtonFocus}
        {...locations}
        setIsClearOriginFocus={setIsClearOriginFocus}
        setIsClearDestinationFocus={setIsClearDestinationFocus}
        isClearOriginFocus={isClearOriginFocus}
        isClearDestinationFocus={isClearDestinationFocus}
        setEnableAriaHiddenFooter={setEnableAriaHiddenFooter}
        navigation={navigation}
        isFullMode={isFullMode}
        goBack={goBack}
        origin={getNavigationProps('Origin', locations.origin)}
        destination={getNavigationProps('Destination', locations.destination)}
      />
    ),
    DateRangeSelector: (
      <Col key="DateRangeSelector" {...dateRangeColumnSettings} ariaHidden={closeButtonFocus}>
        <SingleDateRangePicker
          showLabel={showLabels}
          {...dateRangeSettings}
          {...dateRange}
          onChange={(date) => {
            dateRange.onChange({
              start: date.start,
              end: date.end,
              type: 'departure',
              flexibleDates: date.flexibleDates,
              availableDays: date.availableDays,
              checkedFlexibleDates: date.checkedFlexibleDates
            });
          }}
          defaultValue={dateRange.value}
        />
      </Col>
    ),
    DatePicker: (
      <Col key="DatePicker" {...dateRangeColumnSettings} ariaHidden={closeButtonFocus}>
        <SingleDatePicker
          showLabel={showLabels}
          {...dateRangeSettings}
          {...dateRange}
          defaultValue={dateRange.value.start}
        />
      </Col>
    ),
    CallToAction: (
      <Col key="CallToAction" {...callToActionColumSettings} ariaHidden={closeButtonFocus}>
        <Button {...callToActionSettings}>
          {translate('cmp-travel-restriction-call-to-action', 'Update')}
        </Button>
      </Col>
    )
  };

  const ref = React.useRef();
  const useClassicNavigation = !enableExperimentalNavigation;

  const [classNameDialog, styleDialog] = useStyles(dialogSettings);
  const myStyle = Object.assign(
    {
      width: '100vw',
      height: '100vh',
      padding: 0,
      margin: 0,
      overflow: 'hidden',
      background: 'white'
    },
    styleDialog
  );

  const overlayStyle = {
    zIndex: 999999,
    position: 'fixed',
    top: '0px',
    right: '0px',
    bottom: '0px',
    left: '0px',
    overflow: 'auto'
  };

  const getFilter = (e) => {
    if (isMobile && e === 'OriginDestination') {
      if (navigation === 'Origin' || navigation === 'Destination') {
        return (
          <DialogOverlay style={overlayStyle}>
            <DialogContent className={classNameDialog} style={myStyle}>
              <Box
                position={navigation === 'all' ? 'relative' : undefined}
                focusReferences={ref}
                {...originDestinationContainerSettings}
              >
                <NavigationHeader
                  headerLocation={headerLocation}
                  title={translate(`cmp-header-mobile-${navigation.toLowerCase()}`)}
                  onClickIcon={goBack}
                  styleIcon={{
                    transform: 'rotate(90deg)',
                    display: 'inline-block'
                  }}
                  {...headerSettings}
                />
                <Row
                  gutters
                  {...containerSettings}
                  paddingWidth="15px !important"
                  {...containerOnFullScreenSettings}
                >
                  {elements['OriginDestinationMobile']}
                </Row>
              </Box>
            </DialogContent>
          </DialogOverlay>
        );
      } else {
        return elements['OriginDestinationMobile'];
      }
    }
    return elements[e];
  };

  const filtersToDisplay = availableFilters.filter((e) => elements[e]);

  return (
    <Row className="Content" {...containerSettings}>
      {filtersToDisplay.map((e, i) => {
        if (secondaryFilter !== undefined) {
          if (i === availableFilters.length - 1) {
            return (
              <>
                {secondaryFilter} {complementaryInformation ?? null}
                {getFilter(e)}
              </>
            );
          }
        }
        return <>{getFilter(e)}</>;
      })}
    </Row>
  );
};

export default RouteFilters;
